"use client";

import React from "react";
import { MapPin, Phone, Mail, ArrowRight, Loader, Upload } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  // BPV: after a successful inquiry, forward the visitor to the Microsoft Bookings calendar
  React.useEffect(() => {
    if (!isSubmitted || submitError) return;
    const t = window.setTimeout(() => {
      window.location.href = "https://bookings.cloud.microsoft/bookwithme/user/84640d256b4b476790888f34f0f6d3ce%40bpvconsulting.com/meetingtype/JPcu8sF1Y02Yu8Fuf-_-QA2?anonymous&ismsaljsauthenabled=true";
    }, 1500);
    return () => window.clearTimeout(t);
  }, [isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 bg-card/50 border border-border p-8 rounded-sm">
        <div className="size-16 bg-primary/10 text-primary flex items-center justify-center rounded-full mb-4">
          <ArrowRight className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-bold uppercase tracking-tight text-primary">Inquiry Received</h3>
        <p className="text-muted-foreground font-serif">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 uppercase tracking-widest font-mono font-medium rounded-sm border-primary/20 hover:border-primary">
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Full Name" className="rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-lg h-12 font-serif" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">Please provide your legal name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your.email@company.com" className="rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-lg h-12 font-serif" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">For secure correspondence.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Company or Business Name" className="rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-lg h-12 font-serif" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">The entity seeking advisory.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="+1 (949) 000-0000" className="rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-lg h-12 font-serif" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">Direct line for urgent matters.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="annual_revenue" rules={{
        required: "Please select an annual revenue range"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Annual Revenue</FormLabel>
              <FormControl>
                <select className={cn("flex h-12 w-full min-w-0 rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 py-1 text-lg shadow-none transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 font-serif", "focus-visible:border-primary focus-visible:ring-0", "aria-invalid:border-destructive text-foreground appearance-none cursor-pointer")} {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select Revenue</option>
                  <option value="Less than $5M" className="bg-background text-foreground">Less than $5M</option>
                  <option value="$5M - $10M" className="bg-background text-foreground">$5M - $10M</option>
                  <option value="$10M - $25M" className="bg-background text-foreground">$10M - $25M</option>
                  <option value="$25M - $50M" className="bg-background text-foreground">$25M - $50M</option>
                  <option value="$50M+" className="bg-background text-foreground">$50M+</option>
                </select>
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">Overall scale of operations.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="ebitda_range" rules={{
        required: "Please select an EBITDA range"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Annual EBITDA</FormLabel>
              <FormControl>
                <select className={cn("flex h-12 w-full min-w-0 rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 py-1 text-lg shadow-none transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 font-serif", "focus-visible:border-primary focus-visible:ring-0", "aria-invalid:border-destructive text-foreground appearance-none cursor-pointer")} {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select Range</option>
                  <option value="Less than $1M" className="bg-background text-foreground">Less than $1M</option>
                  <option value="$1M - $3M" className="bg-background text-foreground">$1M - $3M</option>
                  <option value="$3M - $5M" className="bg-background text-foreground">$3M - $5M</option>
                  <option value="$5M - $10M" className="bg-background text-foreground">$5M - $10M</option>
                  <option value="$10M+" className="bg-background text-foreground">$10M+</option>
                </select>
              </FormControl>
              <FormDescription className="text-[10px] font-serif italic">Helps us assign the right partner.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 20,
        message: "Message must be at least 20 characters"
      },
      maxLength: {
        value: 2000,
        message: "Message cannot exceed 2000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Strategic Intent</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your objectives or current situation..." className="min-h-[120px] rounded-none border border-border bg-transparent p-4 focus-visible:ring-0 focus-visible:border-primary text-base resize-y font-serif" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-[10px] font-mono font-semibold text-muted-foreground">Confidential Documents (optional)</FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => {
            onChange(e.target.files);
          }} {...field} />
                <div className="flex items-center gap-3 p-4 border border-dashed border-border bg-card/30 hover:bg-card/50 transition-colors rounded-sm">
                  <Upload className="size-5 text-primary-foreground" />
                  <span className="text-sm text-muted-foreground font-serif">
                    {value && value.length > 0 ? value[0].name : "Upload CIM or financials (Max 5 MB)."}
                  </span>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium font-serif">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto h-14 px-10 text-sm font-mono font-bold uppercase tracking-widest rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Initiate Consultation
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function ReachTheLab() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="246116" id="contact-our-partners" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/30 -skew-x-12 translate-x-24 pointer-events-none hidden lg:block" />
      <div className="absolute bottom-0 left-0 w-64 h-64 border-[1px] border-primary/10 rounded-full -translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24">
          
          <div ref={leftRef} className={cn("lg:col-span-5 flex flex-col justify-between transition-all duration-1000 ease-out", leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-default font-bold uppercase tracking-tighter leading-[0.9] mb-8 text-primary">Contact THE<br /><span data-ast-id="f:/src/pages/Contact/sections/ReachTheLab.tsx|sig:6j0ygg" data-ast-start="12422" data-ast-end="12487" contenteditable="false" style={{
                color: 'rgb(201, 172, 100)'
              }}>BPV Team</span></h2>
              <p className="text-xl text-muted-foreground max-w-md font-serif leading-relaxed mb-12">
                Blue Phoenix Ventures provides discreet, high-stakes advisory for business owners ready to secure their legacy.
              </p>
            </div>

            <div className="space-y-8 mt-auto">
              <div className="group flex items-start gap-4">
                <div className="p-3 bg-card border border-border group-hover:border-primary/50 transition-colors shadow-sm">
                  <MapPin className="size-6 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="text-[10px] font-mono font-bold uppercase tracking-widest text-primary/60 mb-1">Newport Beach HQ</h4>
                  <p className="text-lg font-serif font-medium">500 Newport Center Dr<br />Newport Beach, CA 92660</p>
                </div>
              </div>

              <div className="group flex items-start gap-4">
                <div className="p-3 bg-card border border-border group-hover:border-primary/50 transition-colors shadow-sm">
                  <Phone className="size-6 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="text-[10px] font-mono font-bold uppercase tracking-widest text-primary/60 mb-1">Direct Advisory</h4>
                  <p className="text-lg font-serif font-medium">+1 (949) 698-8293</p>
                </div>
              </div>

              <div className="group flex items-start gap-4">
                <div className="p-3 bg-card border border-border group-hover:border-primary/50 transition-colors shadow-sm">
                  <Mail className="size-6 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="text-[10px] font-mono font-bold uppercase tracking-widest text-primary/60 mb-1">Confidential Inbox</h4>
                  <p className="text-lg font-serif font-medium">Jeff@BPVConsulting.com</p>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={cn("lg:col-span-7 bg-card border border-border p-6 md:p-10 lg:p-12 backdrop-blur-md shadow-2xl transition-all duration-1000 delay-200 ease-out rounded-sm", rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <div className="mb-10">
              <h3 className="text-2xl font-default font-semibold uppercase tracking-tight mb-2 text-primary">Engagement Inquiry</h3>
              <div className="h-0.5 w-16 bg-primary-foreground" />
            </div>

            <FormProvider formKey="partner_contact_form_contact" formId="partner_contact_form_contact" sectionName="Contact Our Partners" defaultValues={{
            full_name: "",
            email: "",
            phone_number: "",
            company_name: "",
            ebitda_range: "",
            annual_revenue: "",
            message: "",
            attachment: undefined
          }} submitText="Initiate Consultation" successMessage="Thank you. Your inquiry has been received. Taking you to our calendar to choose a time for your consultation...">
              <ContactFormInner />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}